import { sunshineAttachmentProxyResponseSchema } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { getExtensionFromContentType } from "@src/appV2/lib/files/utils";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { format } from "date-fns";
import Smooch from "smooch";

function generateDefaultFileName(contentType: string): string {
  const extension = getExtensionFromContentType(contentType);
  const timestamp = format(new Date(), "yyyy-MM-dd-HH-mm-ss");
  return `upload-${timestamp}.${extension}`;
}

interface UploadAttachmentVariables {
  file: UploadFileData;
  conversationId: string;
}

interface UploadAttachmentResult {
  mediaUrl: string;
  mediaType: string;
}

export function useUploadSmoochAttachment(
  options: UseMutationOptions<UploadAttachmentResult, Error, UploadAttachmentVariables> = {}
): UseMutationResult<UploadAttachmentResult, Error, UploadAttachmentVariables> {
  return useMutation({
    mutationFn: async (variables: UploadAttachmentVariables) => {
      const { file, conversationId } = variables;

      // Convert File to ArrayBuffer to send as raw binary data
      const fileBuffer = await file.blob.arrayBuffer();

      const url = `${environmentConfig.REACT_APP_BASE_API_URL}/support/sunshine/${conversationId}/attachments`;
      const { name: fileName, contentType } = file;
      const displayName = isDefined(fileName) ? fileName : generateDefaultFileName(contentType);
      const headers: Record<string, string> = {
        "Content-Type": contentType,
        "X-Original-Filename": displayName,
      };

      const response = await post({
        url,
        data: fileBuffer,
        responseSchema: sunshineAttachmentProxyResponseSchema,
        headers,
      });

      const { mediaUrl, filetype } = response.data.data.attributes;

      const message = {
        type: "file",
        mediaUrl,
        mediaType: filetype,
        text: displayName,
        role: "user",
      };

      Smooch.sendMessage(message, conversationId);

      return { mediaUrl, mediaType: filetype };
    },
    ...options,
  });
}
